// File: frontend/src/pages/search/SearchResultsPage.tsx
// src/pages/search/SearchResultsPage.tsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  MdSchedule,
  MdCancel,
  MdWarning,
} from "react-icons/md";
import {
  CategoryState,
  FullSearchResult,
  SearchAuthorResult,
  SearchCategoryKey,
  SearchClubResult,
  SearchCustomTopicResult,
  SearchEntryResult,
  SearchLeagueResult,
  SearchMatchResult,
  SearchPersonResult,
  SearchTeamResult,
} from "../../models/SearchModel";
import { fullSearch, searchCategory } from "../../services/searchService";
import { generateMatchSlug, generateProfileUrl } from "../../utils/slugGenerator";
import { entryPermalinkPath } from "../../utils/canonicalUrl";
import { entryTopicLabel } from "../../utils/searchUtils";
import { resolveClubName } from "../../services/matchService";
import { SuggestCustomTopicModal } from "../../components/customTopics";
import { useSiteSettings } from "../../context/SiteSettingsContext";
import { useAuth } from "../../context/AuthContext";
import { customTopicService } from "../../services/customTopicService";
import { usePersonRoles } from "../../hooks/usePersonRoles";
import "./SearchResultsPage.css";

// ─── helpers ─────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

const matchSlug = (id: number, home: string, away: string, date: string) =>
  generateMatchSlug({
    id,
    homeTeam: home,
    awayTeam: away,
    matchDate: new Date(date + "T00:00:00"),
  } as any);

// Mirrors the suggest modal / backend typeahead threshold — shorter keywords
// are checked against the loaded Gündem results only
const TOPIC_EXACT_CHECK_MIN_CHARS = 3;

// Initial empty state for a category
function emptyState<T>(): CategoryState<T> {
  return { items: [], hasMore: false, totalElements: 0, page: 0, loading: false };
}

// ─── MatchTitle — renders full "HomeTeam [score/vs/icon] AwayTeam [icon]" row ─

interface MatchTitleProps {
  m: SearchMatchResult;
}

const MatchTitle: React.FC<MatchTitleProps> = ({ m }) => {
  const { t } = useTranslation();
  const status = m.status ?? "COMPLETED";

  const homeName = m.homeTeamTr || m.homeTeam;
  const awayName = m.awayTeamTr || m.awayTeam;

  // Status icon rendered AFTER the away team name
  // Status icon/indicator rendered AFTER away team name
  const renderStatusIcon = () => {
    if (status === "LIVE") {
      // Pulsing red dot after away team name
      return <span className="search-live-dot" aria-hidden="true" />;
    }
    if (status === "POSTPONED") {
      return (
        <span
          className="search-status-icon search-status-icon--postponed"
          title={t("match_status_postponed")}
          aria-label={t("match_status_postponed")}
        >
          <MdSchedule />
        </span>
      );
    }
    if (status === "CANCELLED") {
      return (
        <span
          className="search-status-icon search-status-icon--cancelled"
          title={t("match_status_cancelled")}
          aria-label={t("match_status_cancelled")}
        >
          <MdCancel />
        </span>
      );
    }
    if (status === "ABANDONED") {
      return (
        <span
          className="search-status-icon search-status-icon--abandoned"
          title={t("match_status_abandoned")}
          aria-label={t("match_status_abandoned")}
        >
          <MdWarning />
        </span>
      );
    }
    return null;
  };

  // Middle part between team names — live dot is NOT here, it goes after away team
  const renderMiddle = () => {
    if (status === "COMPLETED" || status === "ABANDONED") {
      return <span className="search-score">{m.homeScore}–{m.awayScore}</span>;
    }
    if (status === "LIVE") {
      return <span className="search-score search-score--live">{m.homeScore}–{m.awayScore}</span>;
    }
    // SCHEDULED, POSTPONED, CANCELLED — no score
    return <span className="search-vs">vs</span>;
  };

  return (
    <div className="search-result-title search-match-title">
      <span>{homeName}</span>
      {renderMiddle()}
      <span>{awayName}</span>
      {renderStatusIcon()}
    </div>
  );
};

// ─── component ────────────────────────────────────────────────────────────────

export const SearchResultsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const q      = searchParams.get("q") ?? "";
  const author = searchParams.get("yazar") ?? "";
  const from   = searchParams.get("baslangic") ?? "";
  const to     = searchParams.get("bitis") ?? "";

  // Active tab
  const [activeTab, setActiveTab] = useState<"all" | SearchCategoryKey>("all");

  // Per-category paginated state
  const [matches,  setMatches]  = useState<CategoryState<SearchMatchResult>>(emptyState());
  const [teams,    setTeams]    = useState<CategoryState<SearchTeamResult>>(emptyState());
  const [entries,  setEntries]  = useState<CategoryState<SearchEntryResult>>(emptyState());
  const [authors,  setAuthors]  = useState<CategoryState<SearchAuthorResult>>(emptyState());
  // Stadiums were removed from search — no state kept for them anymore
  const [leagues,  setLeagues]  = useState<CategoryState<SearchLeagueResult>>(emptyState());
  // Phase-4 addition
  const [people,   setPeople]   = useState<CategoryState<SearchPersonResult>>(emptyState());
  // Phase-5 addition
  const [clubs,    setClubs]    = useState<CategoryState<SearchClubResult>>(emptyState());
  // Phase-6 addition
  const [customTopics, setCustomTopics] = useState<CategoryState<SearchCustomTopicResult>>(emptyState());

  // Global loading flag for the first fetch (all categories at once)
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // "Suggest this as a topic" shortcut shown in the empty state — opens the
  // same modal as the left pane "Başlık Öner" action, pre-filled with the
  // keyword that returned nothing. Guests see the login prompt inside it.
  const [showSuggestModal, setShowSuggestModal] = useState(false);

  // Admin-configurable topic title limit (topic.title.max_length) — the same
  // value the suggestion modal and the backend enforce
  const { topicTitleMaxLength } = useSiteSettings();

  // A keyword longer than the title limit cannot become a topic at all, so
  // the CTA is replaced by a short explanation. Showing a truncated keyword
  // instead would promise a title the user never typed.
  const trimmedQuery = q.trim();
  const queryTooLongForTopic = trimmedQuery.length > topicTitleMaxLength;

  // Topic titles are lowercase-only (sözlük convention) — compare with the
  // Turkish locale so dotted/dotless i is handled correctly
  const normalizedQuery = trimmedQuery.toLocaleLowerCase("tr");

  const { user } = useAuth();

  // md_person_role is the source of truth for role labels — the static
  // person_role_* keys only cover the roles that once lived in the Java enum,
  // so admin-defined roles (e.g. PUNDIT) rendered as raw keys. Same resolution
  // order as PersonRow: master-data name → legacy key → raw code.
  const { translateRole } = usePersonRoles();
  const roleLabel = (code: string): string => {
    const mdName = translateRole(code);
    if (mdName && mdName !== code) return mdName;
    return t(`person_role_${code.toLowerCase()}`, { defaultValue: code });
  };

  // Rookies (çaylaklar) are not allowed to suggest topics — every "Başlık Öner"
  // entry point on this page is hidden for them (backend enforces it as well)
  const isRookie = user?.role?.toUpperCase() === "ROOKIE";

  // Exact Gündem title check for the "Başlık Öner" shortcut.
  // null = still checking (CTA hidden to avoid a flash), true = a topic (or a
  // pending suggestion) with exactly this title exists, false = free to suggest.
  // Only Gündem titles count — matches, people, clubs etc. never block it.
  const [topicExactExists, setTopicExactExists] = useState<boolean | null>(null);
  // Guards against out-of-order exact-check responses
  const exactCheckKeyRef = useRef("");

  // Keep a ref to the current query+filters so stale responses are discarded.
  // The key includes the filters — otherwise switching filters with the same
  // keyword could let an outdated response overwrite the newer one.
  const queryRef = useRef(q);
  const searchKey = `${q}|${author}|${from}|${to}`;

  // ── Apply initial full-search result to all category states ──────────────
  const applyFullResult = useCallback((data: FullSearchResult) => {
    if (data.matches)  setMatches(s  => ({ ...s, items: data.matches!.content,  hasMore: data.matches!.hasMore,  totalElements: data.matches!.totalElements,  page: 0, loading: false }));
    if (data.teams)    setTeams(s    => ({ ...s, items: data.teams!.content,    hasMore: data.teams!.hasMore,    totalElements: data.teams!.totalElements,    page: 0, loading: false }));
    if (data.entries)  setEntries(s  => ({ ...s, items: data.entries!.content,  hasMore: data.entries!.hasMore,  totalElements: data.entries!.totalElements,  page: 0, loading: false }));
    if (data.authors)  setAuthors(s  => ({ ...s, items: data.authors!.content,  hasMore: data.authors!.hasMore,  totalElements: data.authors!.totalElements,  page: 0, loading: false }));
    if (data.leagues)  setLeagues(s  => ({ ...s, items: data.leagues!.content,  hasMore: data.leagues!.hasMore,  totalElements: data.leagues!.totalElements,  page: 0, loading: false }));
    if (data.people)   setPeople(s   => ({ ...s, items: data.people!.content,   hasMore: data.people!.hasMore,   totalElements: data.people!.totalElements,   page: 0, loading: false }));
    if (data.clubs)    setClubs(s    => ({ ...s, items: data.clubs!.content,    hasMore: data.clubs!.hasMore,    totalElements: data.clubs!.totalElements,    page: 0, loading: false }));
    if (data.customTopics) setCustomTopics(s => ({ ...s, items: data.customTopics!.content, hasMore: data.customTopics!.hasMore, totalElements: data.customTopics!.totalElements, page: 0, loading: false }));
  }, []);

  // ── Initial fetch when query or filters change ────────────────────────────
  useEffect(() => {
    // Filter-only searches (author / date range without a keyword) are valid;
    // bail out only when there is neither a keyword nor any filter.
    if (!q.trim() && !author && !from && !to) return;
    queryRef.current = searchKey;

    // Reset all states
    setMatches(emptyState());
    setTeams(emptyState());
    setEntries(emptyState());
    setAuthors(emptyState());
    setLeagues(emptyState());
    setPeople(emptyState());
    setClubs(emptyState());
    setCustomTopics(emptyState());
    setActiveTab("all");
    setError(null);
    // The modal is pre-filled with the previous keyword — close it so a new
    // search never leaves a stale suggestion form open
    setShowSuggestModal(false);
    setInitialLoading(true);

    fullSearch(q.trim(), 0, PAGE_SIZE, { author, from, to })
      .then((data) => {
        if (queryRef.current !== searchKey) return; // discard stale response
        applyFullResult(data);
      })
      .catch(() => {
        if (queryRef.current === searchKey) setError(t("search_error"));
      })
      .finally(() => {
        if (queryRef.current === searchKey) setInitialLoading(false);
      });
  }, [q, author, from, to, searchKey, applyFullResult, t]);

  // ── Exact Gündem title check (drives the top "Başlık Öner" shortcut) ──────
  // Uses the same typeahead endpoint as the suggest modal, so "exists" also
  // covers PENDING suggestions — the modal would block those anyway.
  useEffect(() => {
    exactCheckKeyRef.current = normalizedQuery;
    setTopicExactExists(null);

    // Nothing to check: no keyword, rookie (CTA never shown) or a keyword that
    // can never become a title (handled by the too-long hint instead)
    if (!normalizedQuery || isRookie || queryTooLongForTopic) return;

    // Short keywords are below the typeahead threshold — the loaded Gündem
    // results below are the only source of truth for them
    if (normalizedQuery.length < TOPIC_EXACT_CHECK_MIN_CHARS) {
      setTopicExactExists(false);
      return;
    }

    const checkKey = normalizedQuery;
    customTopicService.typeahead(normalizedQuery)
      .then((res) => {
        if (exactCheckKeyRef.current !== checkKey) return; // discard stale response
        setTopicExactExists(res.exactExists);
      })
      .catch(() => {
        // Convenience check only — the modal and the backend still reject
        // duplicates, so a failed lookup simply allows the CTA
        if (exactCheckKeyRef.current === checkKey) setTopicExactExists(false);
      });
  }, [normalizedQuery, isRookie, queryTooLongForTopic]);

  // ── Load-more for a specific category ────────────────────────────────────
  const loadMore = useCallback(async (cat: SearchCategoryKey) => {
    const setState = {
      matches:  setMatches,
      teams:    setTeams,
      entries:  setEntries,
      authors:  setAuthors,
      leagues:  setLeagues,
      people:   setPeople,
      clubs:    setClubs,
      customTopics: setCustomTopics,
    }[cat] as React.Dispatch<React.SetStateAction<CategoryState<any>>>;

    setState(s => ({ ...s, loading: true }));

    const currentKey = queryRef.current;
    const nextPage = {
      matches:  matches.page,
      teams:    teams.page,
      entries:  entries.page,
      authors:  authors.page,
      leagues:  leagues.page,
      people:   people.page,
      clubs:    clubs.page,
      customTopics: customTopics.page,
    }[cat] + 1;

    try {
      // Pass the active advanced filters so load-more pages stay consistent
      // with the initial filtered result set
      const data = await searchCategory(q.trim(), cat, nextPage, PAGE_SIZE, { author, from, to });
      if (queryRef.current !== currentKey) return;

      const page = (data as any)[cat];
      if (!page) return;

      setState(s => ({
        items:         [...s.items, ...page.content],
        hasMore:       page.hasMore,
        totalElements: page.totalElements,
        page:          nextPage,
        loading:       false,
      }));
    } catch {
      setState(s => ({ ...s, loading: false }));
    }
  }, [q, author, from, to, matches.page, teams.page, entries.page, authors.page, leagues.page, people.page, clubs.page, customTopics.page]);

  // ── Tab counts (from totalElements for accuracy) ──────────────────────────
  const totalAll =
    (matches.totalElements  || matches.items.length)  +
    (teams.totalElements    || teams.items.length)    +
    (entries.totalElements  || entries.items.length)  +
    (authors.totalElements  || authors.items.length)  +
    (people.totalElements   || people.items.length)   +
    (clubs.totalElements    || clubs.items.length)    +
    (customTopics.totalElements || customTopics.items.length);

  // Tab order: Gündem → Kişi → Kulüp → Takım → Maç → Yazar → Tanım.
  // The stadiums tab was removed together with the stadium search category.
  const tabs = [
    { key: "all",          label: t("search_tab_all"),               count: totalAll },
    { key: "customTopics", label: t("search_category_custom_topics"), count: customTopics.totalElements || customTopics.items.length },
    { key: "people",       label: t("search_category_people"),       count: people.totalElements   || people.items.length },
    { key: "clubs",        label: t("search_category_clubs"),        count: clubs.totalElements    || clubs.items.length },
    { key: "teams",        label: t("search_category_teams"),        count: teams.totalElements    || teams.items.length },
    { key: "matches",      label: t("search_category_matches"),      count: matches.totalElements  || matches.items.length },
    { key: "authors",      label: t("search_category_authors"),      count: authors.totalElements  || authors.items.length },
    { key: "entries",      label: t("search_category_entries"),      count: entries.totalElements  || entries.items.length },
  ].filter(tab => tab.key === "all" || tab.count > 0);

  // An exact title among the loaded Gündem results also counts as "exists"
  // (covers keywords below the typeahead threshold)
  const exactTopicInResults = customTopics.items.some(
    (ct) => ct.title.toLocaleLowerCase("tr") === normalizedQuery
  );

  // "Başlık Öner" shortcut: keyword search only, never for rookies, only when
  // the keyword fits the title limit and no Gündem title matches it exactly
  const canSuggestTopic =
    !!trimmedQuery &&
    !isRookie &&
    !queryTooLongForTopic &&
    !initialLoading &&
    !error &&
    topicExactExists === false &&
    !exactTopicInResults;

  // Single "Başlık Öner" block (hint + button) shared by BOTH placements —
  // the top bar (results exist) and the empty state (no results) — so the two
  // can never drift apart in markup, size or layout
  const suggestTopicBlock = (
    <div className="search-results-empty-suggest">
      <p className="search-results-empty-suggest-hint">
        {t("search_no_results_suggest_hint")}
      </p>
      <button
        type="button"
        className="search-suggest-topic-btn"
        onClick={() => setShowSuggestModal(true)}
      >
        <span className="search-suggest-topic-btn__label">
          {t("search_no_results_suggest_cta", { q: trimmedQuery })}
        </span>
      </button>
    </div>
  );

  // ── Load-more button for a category section ───────────────────────────────
  const LoadMoreBtn = ({ cat }: { cat: SearchCategoryKey }) => {
    const state = { matches, teams, entries, authors, leagues, people, clubs, customTopics }[cat];
    if (!state.hasMore) return null;
    return (
      <button
        className="search-load-more-btn"
        onClick={() => loadMore(cat)}
        disabled={state.loading}
        type="button"
      >
        {state.loading ? t("search_loading_more") : t("search_load_more")}
      </button>
    );
  };

  return (
    <div className="search-results-page">
      <div className="search-results-header">
        <span className="search-results-query-label">
          {initialLoading
            ? t("search_searching")
            : totalAll > 0
            ? q.trim()
              ? t("search_results_count", { count: totalAll, q })
              // Filter-only search — the label with the quoted keyword would
              // render an empty "" here, so use a keyword-less variant
              : t("search_results_count_filtered", { count: totalAll })
            : null}
        </span>

        {/* Active filter pills */}
        {(author || from || to) && (
          <div className="search-results-filters">
            {author && (
              <span className="search-filter-pill">
                {t("search_option_author")}: @{author}
                <button onClick={() => { const p = new URLSearchParams(searchParams); p.delete("yazar"); setSearchParams(p); }}>×</button>
              </span>
            )}
            {from && (
              <span className="search-filter-pill">
                {t("search_option_date_from")}: {from}
                <button onClick={() => { const p = new URLSearchParams(searchParams); p.delete("baslangic"); setSearchParams(p); }}>×</button>
              </span>
            )}
            {to && (
              <span className="search-filter-pill">
                {t("search_option_date_to")}: {to}
                <button onClick={() => { const p = new URLSearchParams(searchParams); p.delete("bitis"); setSearchParams(p); }}>×</button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Results exist but none of the Gündem titles matches the keyword
          exactly → offer the keyword as a topic suggestion above the tabs.
          Same flow as the left-pane "Başlık Öner" action, just pre-filled. */}
      {canSuggestTopic && totalAll > 0 && (
        <div className="search-suggest-topic-bar">
          {suggestTopicBlock}
        </div>
      )}

      {/* Tabs */}
      {tabs.length > 0 && (
        <div className="search-results-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`search-results-tab${activeTab === tab.key ? " active" : ""}`}
              onClick={() => setActiveTab(tab.key as any)}
              type="button"
            >
              {tab.label}
              <span className="search-tab-count">{tab.count}</span>
            </button>
          ))}
        </div>
      )}

      {/* Global loading spinner */}
      {initialLoading && (
        <div className="search-results-spinner">
          <div className="search-spinner-dot" />
          <div className="search-spinner-dot" />
          <div className="search-spinner-dot" />
        </div>
      )}

      {/* Error */}
      {error && <div className="search-results-error">{error}</div>}

      {/* Empty */}
      {!initialLoading && !error && totalAll === 0 && q && (
        <div className="search-results-empty">
          <p>{t("search_no_results_for", { q })}</p>
          {/* Nothing found → offer to turn the keyword into a topic suggestion.
              Same flow as the left-pane "Başlık Öner" action, just pre-filled. */}
          {/* Rookies (çaylaklar) cannot suggest topics — neither the CTA nor
              the too-long hint is shown to them */}
          {trimmedQuery && !isRookie && (queryTooLongForTopic ? (
            // Too long to ever be a valid topic title — explain instead of
            // offering an action that could not succeed
            <p className="search-results-empty-suggest-hint">
              {t("search_no_results_suggest_too_long", { max: topicTitleMaxLength })}
            </p>
          ) : canSuggestTopic && suggestTopicBlock)}
        </div>
      )}

      {/* Topic suggestion modal, pre-filled with the failed search keyword */}
      {showSuggestModal && (
        <SuggestCustomTopicModal
          initialTitle={trimmedQuery}
          onClose={() => setShowSuggestModal(false)}
        />
      )}

      {/* Results */}
      {!initialLoading && totalAll > 0 && (
        <div className="search-results-body">

          {/* Section order: Gündem → Kişi → Kulüp → Takım → Maç → Yazar → Tanım.
              The stadiums section was removed from search entirely. */}

          {/* Custom topics ("Gündem") */}
          {(activeTab === "all" || activeTab === "customTopics") && customTopics.items.length > 0 && (
            <section className="search-results-section">
              {activeTab === "all" && <h2 className="search-section-title">{t("search_category_custom_topics")}</h2>}
              <div className="search-results-list">
                {customTopics.items.map((ct) => (
                  <button
                    key={ct.id}
                    className="search-result-card"
                    onClick={() => navigate(`/gundem/${ct.slug}`)}
                    type="button"
                  >
                    <div className="search-result-title">{ct.title}</div>
                    {ct.categoryName && (
                      <div className="search-result-meta">{ct.categoryName}</div>
                    )}
                  </button>
                ))}
              </div>
              <LoadMoreBtn cat="customTopics" />
            </section>
          )}

          {/* People */}
          {(activeTab === "all" || activeTab === "people") && people.items.length > 0 && (
            <section className="search-results-section">
              {activeTab === "all" && <h2 className="search-section-title">{t("search_category_people")}</h2>}
              <div className="search-results-list">
                {people.items.map((p) => (
                  <button
                    key={p.id}
                    className="search-result-card"
                    onClick={() => navigate(`/kisi/${p.slug}`)}
                    type="button"
                  >
                    <div className="search-result-title">
                      {p.knownName ?? `${p.firstName} ${p.lastName}`}
                    </div>
                    <div className="search-result-meta">
                      {p.latestRole ? roleLabel(p.latestRole) : ""}
                      {p.latestClub ? ` · ${p.latestClub}` : ""}
                      {p.nationality ? ` · ${p.nationality}` : ""}
                    </div>
                  </button>
                ))}
              </div>
              <LoadMoreBtn cat="people" />
            </section>
          )}

          {/* Clubs */}
          {(activeTab === "all" || activeTab === "clubs") && clubs.items.length > 0 && (
            <section className="search-results-section">
              {activeTab === "all" && <h2 className="search-section-title">{t("search_category_clubs")}</h2>}
              <div className="search-results-list">
                {clubs.items.map((c) => (
                  <button
                    key={c.clubId}
                    className="search-result-card"
                    onClick={() => navigate(`/kulup/${c.slug}`)}
                    type="button"
                  >
                    <div className="search-result-title">
                      {resolveClubName(c)}
                    </div>
                    <div className="search-result-meta">
                      {c.shortName}
                    </div>
                  </button>
                ))}
              </div>
              <LoadMoreBtn cat="clubs" />
            </section>
          )}

          {/* Teams */}
          {(activeTab === "all" || activeTab === "teams") && teams.items.length > 0 && (
            <section className="search-results-section">
              {activeTab === "all" && <h2 className="search-section-title">{t("search_category_teams")}</h2>}
              <div className="search-results-list">
                {teams.items.map((tm) => {
                  // Build the team URL slug the same way TeamModel.getTeamUrlSlug does
                  const SPORT_SLUG_MAP: Record<string, string> = {
                    FOOTBALL:          "futbol",
                    BASKETBALL:        "basketbol",
                    VOLLEYBALL:        "voleybol",
                    AMERICAN_FOOTBALL: "amerikan-futbol",
                    BASEBALL:          "beyzbol",
                    ICE_HOCKEY:        "buz-hokeyi",
                    HANDBALL:          "hentbol",
                  };
                  const sportPart    = tm.sportType ? (SPORT_SLUG_MAP[tm.sportType] ?? tm.sportType.toLowerCase()) : "futbol";
                  const genderSuffix = tm.gender === "FEMALE" ? "-kadin" : tm.gender === "MIXED" ? "-karma" : "";
                  const codeSuffix   = tm.teamCode ? `-${tm.teamCode}` : "";
                  const teamUrlSlug  = `${sportPart}${genderSuffix}${codeSuffix}`;

                  return (
                    <button
                      key={tm.teamId}
                      className="search-result-card"
                      onClick={() => navigate(`/takim/${tm.clubSlug}/${teamUrlSlug}`)}
                      type="button"
                    >
                      <div className="search-result-title">{tm.displayName || tm.nameTr || tm.name}</div>
                      <div className="search-result-meta">
                        {tm.sportType ? t(`sport_type_${tm.sportType.toLowerCase()}`) : ""}
                        {tm.gender === "FEMALE" ? ` · ${t("match_gender_female")}` : tm.gender === "MALE" ? ` · ${t("match_gender_male")}` : tm.gender === "MIXED" ? ` · ${t("match_gender_mixed")}` : ""}
                        {tm.teamCode ? ` · ${tm.teamCode.toUpperCase()}` : ""}
                      </div>
                    </button>
                  );
                })}
              </div>
              <LoadMoreBtn cat="teams" />
            </section>
          )}

          {/* Matches */}
          {(activeTab === "all" || activeTab === "matches") && matches.items.length > 0 && (
            <section className="search-results-section">
              {activeTab === "all" && <h2 className="search-section-title">{t("search_category_matches")}</h2>}
              <div className="search-results-list">
                {matches.items.map((m) => (
                  <button
                    key={m.id}
                    className="search-result-card"
                    onClick={() => navigate(`/${matchSlug(m.id, m.homeTeam, m.awayTeam, m.matchDate)}`)}
                    type="button"
                  >
                    <MatchTitle m={m} />
                    <div className="search-result-meta">
                      {new Date(m.matchDate + "T00:00:00").toLocaleDateString("tr-TR")}
                      {m.league ? ` · ${m.league}` : ""}
                    </div>
                  </button>
                ))}
              </div>
              <LoadMoreBtn cat="matches" />
            </section>
          )}

          {/* Authors */}
          {(activeTab === "all" || activeTab === "authors") && authors.items.length > 0 && (
            <section className="search-results-section">
              {activeTab === "all" && <h2 className="search-section-title">{t("search_category_authors")}</h2>}
              <div className="search-results-list">
                {authors.items.map((au) => (
                  <button
                    key={au.userId}
                    className="search-result-card"
                    onClick={() => navigate(generateProfileUrl(au.username, au.userId))}
                    type="button"
                  >
                    <div className="search-result-title">@{au.username}</div>
                    <div className="search-result-meta">
                      {/* Role label goes through i18n like everywhere else — the raw
                          enum was leaking the English name into the Turkish UI */}
                      {au.entryCount} {t("search_author_entry_count")} · {t(`status_${au.role.toLowerCase()}`)}
                    </div>
                  </button>
                ))}
              </div>
              <LoadMoreBtn cat="authors" />
            </section>
          )}

          {/* Entries — always the last section in the "all" tab */}
          {(activeTab === "all" || activeTab === "entries") && entries.items.length > 0 && (
            <section className="search-results-section">
              {activeTab === "all" && <h2 className="search-section-title">{t("search_category_entries")}</h2>}
              <div className="search-results-list">
                {entries.items.map((en) => (
                  <button
                    key={en.entryId}
                    className="search-result-card"
                    onClick={() => {
                      // The permalink route resolves the topic AND the page the
                      // entry sits on. Building a topic URL here could only ever
                      // produce page 1, where the anchor may not exist.
                      navigate(entryPermalinkPath(en.entryId));
                    }}
                    type="button"
                  >
                    <div className="search-result-snippet">{en.snippet}</div>
                    <div className="search-result-meta">
                      @{en.authorUsername}
                      {entryTopicLabel(en) && ` · ${entryTopicLabel(en)}`}
                      {en.topicType === "MATCH" && en.matchDate &&
                        ` · ${new Date(en.matchDate + "T00:00:00").toLocaleDateString("tr-TR")}`}
                    </div>
                  </button>
                ))}
              </div>
              <LoadMoreBtn cat="entries" />
            </section>
          )}

          {/* Leagues section removed: there is no dedicated league/cup page,
              so these results were showing up as effectively unclickable. */}

        </div>
      )}
    </div>
  );
};